'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();

import { clsx } from 'clsx/lite'

import { AppUiVisual } from '../app-ui-visual'
import { CURRENT_MONTH_INDEX, HOURS_EMPLOYEE, teamMember } from '../demo-data'
import { useVisualCopy, type VisualCopy } from '../visual-copy'
import { AppHeader, AppScreen, HeaderStat } from './app-screen'
import styles from './employee-hours-visual.module.css'
import { useScenario } from './use-scenario'

/** One employee's weekly bars, from the shared hours data. */
const WEEKS: ReadonlyArray<readonly [number, number, number]> = HOURS_EMPLOYEE.weeks

/**
 * The chart is scaled so its tallest week reaches the top of the plot.
 *
 * The figures are percentages of a full week, and nobody's fullest week was near 100, so the bars
 * sat along the bottom with half the panel empty above them — a chart hugging the floor of a box
 * reads as a layout fault rather than as data. Scaling is honest here because the axis is
 * comparative: what matters is how the weeks stand against each other.
 */
const TALLEST_WEEK = Math.max(...WEEKS.map(([u, v, z]) => u + v + z))
const PLOT_SCALE = TALLEST_WEEK > 0 ? 100 / TALLEST_WEEK : 1

/** The months so far this year, from the shared list. */
const months = (t: VisualCopy) => t.monthsShort.slice(0, CURRENT_MONTH_INDEX + 1)
// The colleague charted here, and her/his contract — from the shared team + hours data.
const EMPLOYEE = teamMember(HOURS_EMPLOYEE.member)
const CONTRACT_DAYS = HOURS_EMPLOYEE.contractDays

const TOTAL_BEATS = 12 // 12s; beats 6-8 hold the contract card long enough to read
const REST_BEAT = 9 // SSR / reduced-motion snapshot — all bars grown, totals final, card hidden
const RESET_BEAT = 11 // final loop beat already reads as the empty start, so the wrap is invisible

const GROW_FROM = 1
const VERLOF_FROM = 2
const ZIEK_FROM = 3
const CARD_FROM = 5

/**
 * Urenregistratie per employee — one colleague's (Thijs van den Berg) year in 18 weekly stacked
 * bars (worked / leave / sick), a totals row (hours, sick, leave balance, overtime), and a contract
 * card that slides in. Faithful to Anim Medewerker Uren: 12-beat loop at 1000ms; `beat === null`
 * (SSR, no-JS, reduced motion) renders the resolved rest state (all bars grown, totals final).
 */
export function EmployeeHoursVisual({ className }: { className?: string }) {
  const { t } = useVisualCopy()
  const { ref, beat } = useScenario(TOTAL_BEATS, { intervalMs: 1000, restBeat: REST_BEAT })
  // Same idiom as every sibling: the final beat renders as beat 0, so 11 -> 0 changes nothing.
  const raw = beat ?? REST_BEAT
  const step = raw >= RESET_BEAT ? 0 : raw
  const on = (at: number) => step >= at

  const grown = on(GROW_FROM)
  const leave = on(VERLOF_FROM)
  const sick = on(ZIEK_FROM)
  const cardVisible = on(CARD_FROM) && step < REST_BEAT

  const totalHours = sick ? HOURS_EMPLOYEE.totalHours : grown ? '984,50' : '0,00'
  const totalSick = sick ? HOURS_EMPLOYEE.sickHours : '0,00'
  const leaveBalance = sick ? HOURS_EMPLOYEE.leaveBalance : '200,00'
  const overtime = sick ? HOURS_EMPLOYEE.overtime : '0,00'

  return (
    <AppUiVisual label={t.screen.employeeHours.caption} className={className}>
      <AppScreen>
        <div ref={ref} data-visual="employee-hours" className={clsx(styles.root, 'relative flex h-full flex-col')}>
          <AppHeader
            title={t.screen.employeeHours.title}
            detail={EMPLOYEE.name}
            right={<HeaderStat value={totalHours} label={t.word.hoursUnit} />}
          />

          <div className="flex min-h-0 flex-1 items-end gap-[2px] px-3.5 pt-2.5 pb-1">
            {WEEKS.map(([u, v, z], i) => {
              // The rounded top belongs to whichever segment is on top of the stack this week, not
              // to sickness — most weeks have none, and those bars ended in a square edge while the
              // few with a red cap were the only rounded ones on the chart.
              const sickHeight = sick ? z : 0
              const leaveHeight = leave ? v : 0
              return (
                <div key={i} className="flex h-full flex-1 flex-col justify-end gap-px">
                  <div
                    className={clsx(styles.bar, sickHeight > 0 && 'rounded-t-[2px]')}
                    style={{ height: `${sickHeight * PLOT_SCALE}%`, background: 'var(--eh-ziek)' }}
                  />
                  <div
                    className={clsx(styles.bar, sickHeight === 0 && leaveHeight > 0 && 'rounded-t-[2px]')}
                    style={{ height: `${leaveHeight * PLOT_SCALE}%`, background: 'var(--eh-verlof)' }}
                  />
                  <div
                    className={clsx(
                      styles.bar,
                      'rounded-b-[1px]',
                      sickHeight === 0 && leaveHeight === 0 && 'rounded-t-[2px]',
                    )}
                    style={{ height: `${(grown ? u : 6) * PLOT_SCALE}%`, background: 'var(--eh-uren)' }}
                  />
                </div>
              )
            })}
          </div>

          <div className="flex justify-between px-3.5 pb-1.5 text-[7px]" style={{ color: 'var(--au-text2)' }}>
            {months(t).map((m) => (
              <span key={m}>{m}</span>
            ))}
          </div>

          <div className="grid grid-cols-4 border-t" style={{ borderColor: 'var(--au-hairline)' }}>
            <Stat label={t.screen.employeeHours.hoursTotal} value={totalHours} />
            <Stat label={t.screen.employeeHours.sickHours} value={totalSick} bordered />
            <Stat label={t.screen.employeeHours.leaveBalance} value={leaveBalance} color="var(--au-done)" bordered />
            {/* Red only when the balance is actually negative. Zero overtime is not a problem
                state, and colouring it as one made the tile read as a warning at rest. */}
            <Stat
              label={t.screen.employeeHours.overtime}
              value={overtime}
              // Both the ASCII hyphen and the typographic minus the formatter actually emits.
              color={/^[-\u2212]/.test(overtime.trim()) ? 'var(--au-late)' : undefined}
              bordered
            />
          </div>

          <div
            className={clsx(
              styles.card,
              'absolute top-[46px] right-3 flex w-[186px] flex-col gap-2 rounded-[11px] border p-3',
            )}
            style={{
              background: 'var(--au-surface)',
              borderColor: 'var(--au-border)',
              boxShadow: '0 12px 30px rgba(20,23,28,0.2)',
              opacity: cardVisible ? 1 : 0,
              transform: cardVisible ? 'translateY(0)' : 'translateY(8px)',
              pointerEvents: 'none',
            }}
            aria-hidden="true"
          >
            <div className="flex items-center justify-between">
              <span className="text-[9px] font-bold tracking-[0.04em]" style={{ color: 'var(--au-text2)' }}>
                CONTRACT
              </span>
              <span
                className="rounded-[6px] px-[9px] py-[2.5px] text-[10px] font-bold text-white tabular-nums"
                style={{ background: 'var(--au-accent)' }}
              >
                40,00 u
              </span>
            </div>
            <span className="text-[12px] font-semibold" style={{ color: 'var(--au-text)' }}>
              {t.screen.employeeHours.fulltime}
            </span>
            <span className="text-[9px]" style={{ color: 'var(--au-text2)' }}>
              {t.screen.employeeHours.since}
            </span>
            <div className="flex gap-[3px] pt-0.5">
              {CONTRACT_DAYS.map((d) => (
                <span
                  key={d}
                  className="flex-1 rounded-[5px] py-1 text-center text-[8.5px] font-semibold"
                  style={{ color: 'var(--au-accent)', background: 'var(--au-accent-soft)' }}
                >
                  {d}
                </span>
              ))}
            </div>
          </div>
        </div>
      </AppScreen>
    </AppUiVisual>
  )
}

function Stat({ label, value, color, bordered }: { label: string; value: string; color?: string; bordered?: boolean }) {
  return (
    <div
      className={clsx('flex flex-col items-center gap-[3px] px-1.5 pt-2.5 pb-3 text-center', bordered && 'border-l')}
      style={{ borderColor: 'var(--au-hairline)' }}
    >
      <span className="text-[8px] font-bold tracking-[0.05em]" style={{ color: 'var(--au-text2)' }}>
        {label}
      </span>
      <b className="text-[13px] font-bold tabular-nums" style={{ color: color ?? 'var(--au-text)' }}>
        {value}
      </b>
    </div>
  )
}
